import { FC, useEffect } from "react";
import { Fab, FormHelperText, Stack, TextField } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { isNumber } from "lodash";
import { Controller, useFormContext } from "react-hook-form";

interface IQuantityInputProps {
  name: string;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
  allowDecimals?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  dataTestId?: string;
}

const QuantityInput: FC<IQuantityInputProps> = ({
  name,
  min,
  max,
  step = 1,
  defaultValue = 0,
  allowDecimals = false,
  readOnly = false,
  disabled = false,
  dataTestId,
}) => {
  const { control, setValue, trigger } = useFormContext();

  const onIncrement = (value: number) => {
    if (!readOnly) {
      setValue(name, isNumber(max) && max <= value ? max : value + step);
      trigger(name);
    }
  };

  const onDecrement = (value: number) => {
    if (!readOnly) {
      setValue(name, isNumber(min) && min >= value ? min : value - step);
      trigger(name);
    }
  };

  useEffect(() => {
    trigger(name);
  }, [name, trigger]);

  const checkValue = (value: string) => {
    let newValue = 0;

    if (allowDecimals) {
      newValue = parseFloat(value.replace(",", "."));
    } else {
      newValue = parseInt(value, 10);
    }

    if (Number.isNaN(newValue)) {
      newValue = 0;
    } else {
      // Round to two decimal places
      newValue = Math.round(newValue * 100) / 100;
    }

    if (isNumber(min) && newValue <= min) {
      newValue = min;
    }
    if (isNumber(max) && newValue >= max) {
      newValue = max;
    }

    return newValue;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field, fieldState: { invalid, error } }) => (
        <Stack
          position="relative"
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap="0.5rem"
          marginTop="1rem"
          padding="0 1rem 1rem 0"
        >
          <Fab
            size="small"
            disabled={
              disabled ||
              readOnly ||
              (isNumber(min) ? field.value <= min : undefined)
            }
            disableRipple
            onClick={() => onDecrement(field.value)}
          >
            <RemoveIcon />
          </Fab>
          <TextField
            {...field}
            onChange={(event) => field.onChange(event.target.value)}
            onBlur={(event) => field.onChange(checkValue(event.target.value))}
            size="small"
            disabled={disabled}
            sx={{
              "& input": {
                textAlign: "center",
                maxWidth: "80px",
              },
            }}
            error={invalid}
            inputRef={field.ref}
            slotProps={{
              input: {
                readOnly,
              },
            }}
            data-testid={dataTestId}
          />
          <Fab
            size="small"
            disabled={
              disabled ||
              readOnly ||
              (isNumber(max) ? field.value >= max : undefined)
            }
            disableRipple
            onClick={() => onIncrement(field.value)}
          >
            <AddIcon />
          </Fab>
          {invalid && (
            <FormHelperText
              error={invalid}
              // absolute positioning to avoid displacing flex container
              sx={{ position: "absolute", bottom: "-0.5rem", left: 0 }}
            >
              {error?.message}
            </FormHelperText>
          )}
        </Stack>
      )}
    />
  );
};

export default QuantityInput;
